import { Link, useLocation } from 'react-router'
import { TruncatedText } from '@/components/commons/truncated-text'
import { cn } from '@/helpers/utils'

interface AttendeeNameLinkProps {
	name: string
	/** Detail-page target; without it the name renders as plain (truncating) text. */
	to?: string
	className?: string
}

/**
 * Attendee name shown as an underlined link to the detail page — the underline
 * signals the row is openable. Truncates like {@link TruncatedText} and stops
 * click propagation so it navigates even inside a row that has its own click
 * handler (e.g. an accordion toggle). Falls back to plain text without a target.
 *
 * Records the current tab path as `state.from` so the detail page's back button
 * and breadcrumb return to the exact table (attendees / check-in / export) the
 * user came from.
 */
export function AttendeeNameLink({
	name,
	to,
	className
}: AttendeeNameLinkProps) {
	const { pathname } = useLocation()
	if (!to) {
		return <TruncatedText className={className}>{name}</TruncatedText>
	}
	return (
		<Link
			className={cn(
				'block truncate underline underline-offset-2 hover:opacity-70',
				className
			)}
			onClick={(event) => event.stopPropagation()}
			state={{ from: pathname }}
			title={name}
			to={to}
		>
			{name}
		</Link>
	)
}
